import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { SpendingBreakdownSheet } from "../components/SpendingBreakdownSheet";
import { AISummary } from "../components/AISummary";
import { ShortfallAlert } from "../components/ShortfallAlert";
import { 
  Wallet, 
  CreditCard, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Calendar,
  Receipt,
  Target,
  Lightbulb,
  BarChart3,
  GraduationCap,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  AlertCircle,
  Flag,
  Zap
} from "lucide-react";

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accountsSummary, setAccountsSummary] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // null = auto (most recent with data)
  const [monthLoading, setMonthLoading] = useState(false);
  const [upcomingBills, setUpcomingBills] = useState([]);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [netWorth, setNetWorth] = useState(null);
  const [leftover, setLeftover] = useState(null);
  const [quickActions, setQuickActions] = useState({ subscriptions: [], goals: [], debtProgress: null });
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [chartPeriod, setChartPeriod] = useState(() => {
    try { return localStorage.getItem("tally_chart_period") || "1M"; } catch { return "1M"; }
  });
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsRes, summaryRes, billsRes, calendarRes, netWorthRes, leftoverRes, subsRes, goalsRes, debtRes] = await Promise.all([
        api.get("/accounts/summary"),
        api.get("/transactions/summary/monthly"),
        api.get("/bills/upcoming"),
        api.get("/calendar/connections"),
        api.get("/net-worth"),
        api.get("/leftover"),
        api.get("/subscriptions"),
        api.get("/goals"),
        api.post("/debt/payoff-plan?extra_payment=0&strategy=avalanche")
      ]);

      setAccountsSummary(accountsRes.data);
      setMonthlySummary(summaryRes.data);
      setUpcomingBills(billsRes.data.slice(0, 3));
      setCalendarConnected(calendarRes.data.some(c => c.status === "connected"));
      setNetWorth(netWorthRes.data);
      setLeftover(leftoverRes.data);
      
      // Quick Actions data
      const flaggedSubs = subsRes.data.filter(s => s.flagged_for_cancel);
      const nearGoals = goalsRes.data.filter(g => g.progress_percentage >= 75 && g.progress_percentage < 100);
      setQuickActions({
        subscriptions: flaggedSubs,
        goals: nearGoals,
        debtProgress: debtRes.data?.total_debt > 0 ? debtRes.data : null
      });
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch monthly summary for a specific month
  const fetchMonthlySummary = useCallback(async (month) => {
    setMonthLoading(true);
    try {
      const params = month ? { month } : {};
      const response = await api.get("/transactions/summary/monthly", { params });
      setMonthlySummary(response.data);
    } catch (error) {
      console.error("Failed to fetch monthly summary:", error);
    } finally {
      setMonthLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch chart data
  const fetchChartData = useCallback(async (period) => {
    setChartLoading(true);
    try {
      const res = await api.get("/balance-history", { params: { period } });
      setChartData(res.data);
    } catch (e) {
      console.error("Chart data error:", e);
    } finally {
      setChartLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchChartData(chartPeriod);
  }, [chartPeriod, fetchChartData]);

  // Sync the saved chart range from the server (follows the user across devices)
  useEffect(() => {
    let active = true;
    api.get("/user/chart-period")
      .then((res) => {
        const p = res.data?.period;
        if (active && p && ["1W", "1M", "3M", "6M", "1Y"].includes(p)) {
          setChartPeriod(p);
          try { localStorage.setItem("tally_chart_period", p); } catch (e) {}
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [api]);

  const selectChartPeriod = useCallback((p) => {
    setChartPeriod(p);
    try { localStorage.setItem("tally_chart_period", p); } catch (e) {}
    api.put("/user/chart-period", { period: p }).catch(() => {});
  }, [api]);

  // Navigate to previous month
  const goToPreviousMonth = () => {
    // Use selectedMonth if set, otherwise use the displayed month from summary
    const baseMonth = selectedMonth || monthlySummary?.month;
    const currentMonthDate = baseMonth 
      ? new Date(baseMonth + "-01")
      : new Date();
    currentMonthDate.setMonth(currentMonthDate.getMonth() - 1);
    const newMonth = currentMonthDate.toISOString().slice(0, 7);
    setSelectedMonth(newMonth);
    fetchMonthlySummary(newMonth);
  };

  // Navigate to next month
  const goToNextMonth = () => {
    // Use selectedMonth if set, otherwise use the displayed month from summary
    const baseMonth = selectedMonth || monthlySummary?.month;
    const currentMonthDate = baseMonth 
      ? new Date(baseMonth + "-01")
      : new Date();
    currentMonthDate.setMonth(currentMonthDate.getMonth() + 1);
    const newMonth = currentMonthDate.toISOString().slice(0, 7);
    
    // Don't go beyond current month
    const now = new Date();
    const nowMonth = now.toISOString().slice(0, 7);
    if (newMonth > nowMonth) return;
    
    setSelectedMonth(newMonth);
    fetchMonthlySummary(newMonth);
  };

  // Check if we can navigate to next month
  const canGoNext = () => {
    const baseMonth = selectedMonth || monthlySummary?.month;
    if (!baseMonth) return false;
    const currentMonthDate = new Date(baseMonth + "-01");
    currentMonthDate.setMonth(currentMonthDate.getMonth() + 1);
    const nextMonth = currentMonthDate.toISOString().slice(0, 7);
    const nowMonth = new Date().toISOString().slice(0, 7);
    return nextMonth <= nowMonth;
  };

  // Get the currently displayed month (for the header)
  const displayedMonth = selectedMonth || monthlySummary?.month;

  const formatCurrency = (amount, showSign = false) => {
    const currency = user?.currency || "CAD";
    const formatted = new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount));
    
    if (showSign && amount !== 0) {
      return amount > 0 ? `+${formatted}` : `-${formatted}`;
    }
    return formatted;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    
    return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  };

  const firstName = user?.name?.split(" ")[0] || "there";
  const currentMonth = new Date().toLocaleDateString("en-CA", { month: "long" });

  if (loading) {
    return (
      <div className="space-y-8" data-testid="dashboard-loading">
        <div>
          <Skeleton className="h-8 w-56 mb-2" />
          <Skeleton className="h-5 w-72" />
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-40 rounded-xl md:col-span-2" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-56 rounded-xl" />
          <Skeleton className="h-56 rounded-xl" />
        </div>
      </div>
    );
  }

  const netPositive = (monthlySummary?.net || 0) >= 0;



  const totalBalance = (accountsSummary?.total_cash || 0) - (accountsSummary?.total_credit_balance || 0);
  const incomeAmt = monthlySummary?.income || 0;
  const spendingAmt = monthlySummary?.spending || 0;
  const netAmt = monthlySummary?.net || 0;
  const balanceDown = totalBalance < 0 || netAmt < 0;

  const getGreeting = () => {
    var h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div data-testid="dashboard">
      {/* Page Header */}
      <div className="phead"><div>
        <h1>{getGreeting()}, {firstName}</h1>
        <div className="sub">Everything's synced and up to date.</div>
      </div></div>

      {/* Hero Balance Card */}
      <div className="card hero" style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"26px 28px 0",flexWrap:"wrap",gap:16}}>
          <div>
            <span className="lab" style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:"#7d7d70"}}>Total balance</span>
            <div style={{fontFamily:"'Lora',Georgia,serif",fontSize:44,fontWeight:600,letterSpacing:"-1.5px",margin:"4px 0"}}>
              {formatCurrency(chartData?.current ?? totalBalance)}
              {chartData && <span style={{display:"inline-flex",alignItems:"center",gap:5,fontFamily:"'Hanken Grotesk',sans-serif",fontSize:14,fontWeight:500,marginLeft:10,color: chartData.change >= 0 ? "#3F7D52" : "#B0603F"}}>
                {chartData.change >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
                {chartData.change >= 0 ? "+" : ""}{formatCurrency(chartData.change)} · {chartPeriod === "1W" ? "7d" : chartPeriod === "1M" ? "30d" : chartPeriod === "3M" ? "90d" : chartPeriod === "6M" ? "6mo" : "1yr"}
              </span>}
            </div>
            <div className="muted" style={{fontSize:"14.5px"}}>Across {accountsSummary?.accounts_count || 0} connected account{accountsSummary?.accounts_count !== 1 ? "s" : ""}</div>
          </div>
          <div className="chips" style={{display:"flex",gap:4}}>
            {["1W","1M","3M","6M","1Y"].map(function(p) {
              return <button key={p} onClick={function(){selectChartPeriod(p);}} style={{height:32,padding:"0 14px",borderRadius:50,border: chartPeriod===p ? "none" : "1.5px solid #ECE8DC",background: chartPeriod===p ? "#14432B" : "#fff",color: chartPeriod===p ? "#F4EFE4" : "#7d7d70",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{p}</button>;
            })}
          </div>
        </div>
        <div style={{padding:"16px 28px 8px",position:"relative"}}>
          {chartLoading ? (
            <div style={{height:190,display:"flex",alignItems:"center",justifyContent:"center",color:"#7d7d70",fontSize:13}}>Loading chart...</div>
          ) : chartData && chartData.points && chartData.points.length > 1 ? (function() {
            var pts = chartData.points;
            var vals = pts.map(function(p){return p.balance;});
            var minV = Math.min.apply(null, vals);
            var maxV = Math.max.apply(null, vals);
            var range = maxV - minV;
            var W = 1000, H = 170, padY = 16, padX = 4;
            var flat = range <= 0;
            var coords = pts.map(function(p, i) {
              var x = padX + (i / (pts.length - 1)) * (W - padX * 2);
              var y = flat ? H / 2 : padY + (1 - (p.balance - minV) / range) * (H - padY * 2);
              return [x, y];
            });
            // Build smooth path
            var pathParts = ["M" + coords[0][0] + "," + coords[0][1]];
            for (var i = 1; i < coords.length; i++) {
              var cp1x = coords[i-1][0] + (coords[i][0] - coords[i-1][0]) * 0.4;
              var cp2x = coords[i][0] - (coords[i][0] - coords[i-1][0]) * 0.4;
              pathParts.push("C" + cp1x + "," + coords[i-1][1] + " " + cp2x + "," + coords[i][1] + " " + coords[i][0] + "," + coords[i][1]);
            }
            var linePath = pathParts.join(" ");
            var areaPath = linePath + " L" + coords[coords.length-1][0] + "," + H + " L" + coords[0][0] + "," + H + " Z";
            var lastPt = coords[coords.length - 1];
            // X-axis labels (first, middle, last) rendered as HTML so they stay crisp
            var fmtD = function(d) { return new Date(d).toLocaleDateString("en-CA", {month:"short",day:"numeric"}); };
            var xLabels = [fmtD(pts[0].date), fmtD(pts[Math.floor(pts.length/2)].date), fmtD(pts[pts.length-1].date)];

            return (
              <div>
                <svg viewBox={"0 0 " + W + " " + H} width="100%" height="170" preserveAspectRatio="none" style={{display:"block"}}>
                  <defs><linearGradient id="ag2" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#7ED957" stopOpacity="0.22" /><stop offset="0.95" stopColor="#7ED957" stopOpacity="0" /></linearGradient></defs>
                  <path d={areaPath} fill="url(#ag2)" />
                  <path d={linePath} fill="none" stroke="#2f6e46" strokeWidth="2.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx={lastPt[0]} cy={lastPt[1]} r="4.5" fill="#14432B" vectorEffect="non-scaling-stroke" />
                  <circle cx={lastPt[0]} cy={lastPt[1]} r="8" fill="#14432B" fillOpacity="0.12" vectorEffect="non-scaling-stroke" />
                </svg>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:8,fontSize:12,fontWeight:500,color:"#a6a698",fontFamily:"'Hanken Grotesk',sans-serif"}}>
                  <span>{xLabels[0]}</span>
                  <span>{xLabels[1]}</span>
                  <span>{xLabels[2]}</span>
                </div>
              </div>
            );
          })() : (
            <div style={{height:190,display:"flex",alignItems:"center",justifyContent:"center",color:"#7d7d70",fontSize:13}}>No balance history yet</div>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="card stats" style={{marginBottom:20}}>
        <div className="st"><div className="lab">Income · this month</div><div className="v" style={{color:"#3F7D52"}}>{formatCurrency(incomeAmt)}</div></div>
        <div className="st"><div className="lab">Spending · this month</div><div className="v">{formatCurrency(spendingAmt)}</div></div>
        <div className="st"><div className="lab">Net · this month</div><div className="v" style={{color: netAmt >= 0 ? "#3F7D52" : "#B0603F"}}>{netAmt >= 0 ? "" : "−"}{formatCurrency(Math.abs(netAmt))}</div></div>
      </div>

      {/* AI Insight */}
      <AISummary page="dashboard" />

      {/* Shortfall Alert */}
      <ShortfallAlert />

      {/* Accounts + Top Spending Grid */}
      <div className="grid g-acct">
        <div className="card">
          <div style={{padding:"20px 22px 4px"}} className="sec-h"><h3>Accounts</h3>
            <span className="link" onClick={function(){navigate("/settings");}} style={{cursor:"pointer"}}>Manage <ChevronRight size={15} /></span>
          </div>
          {accountsSummary && (
            <>
              <div className="acct"><div className="ai2"><Wallet size={20} stroke="#3F7D52" /></div><div className="nm"><b>Cash Accounts</b><small>{accountsSummary.accounts_count} connected</small></div><div className="bal">{formatCurrency(accountsSummary.total_cash)}</div></div>
              {accountsSummary.total_credit_balance > 0 && <div className="acct"><div className="ai2"><CreditCard size={20} stroke="#3F7D52" /></div><div className="nm"><b>Credit Cards</b><small>Balance owing</small></div><div className="bal" style={{color:"#B0603F"}}>−{formatCurrency(accountsSummary.total_credit_balance)}</div></div>}
            </>
          )}
          <div className="trust" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:14,fontSize:"12.5px",color:"#7d7d70",borderTop:"1px solid #ECE8DC"}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7d7d70" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            Secured with 256-bit encryption · read-only access
          </div>
        </div>
        <div className="card">
          <div style={{padding:"20px 22px 6px"}} className="sec-h"><h3>Top spending</h3><span className="muted" style={{fontSize:13}}>This month</span></div>
          {monthlySummary?.top_categories?.slice(0,3).map(function(cat, i) {
            var maxAmt = monthlySummary.top_categories[0]?.amount || 1;
            var pct = Math.round((cat.amount / maxAmt) * 100);
            return <div key={i} className="spend-row" style={{padding:"10px 22px"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:14}}><span>{cat.category}</span><b>{formatCurrency(cat.amount)}</b></div>
              <div className="spbar" style={{height:8,borderRadius:8,background:"#F4EFE4"}}><i style={{width:pct+"%",display:"block",height:"100%",borderRadius:8,background:"#14432B"}} /></div>
            </div>;
          })}
          {(!monthlySummary?.top_categories || monthlySummary.top_categories.length === 0) && <div style={{padding:"30px 22px",textAlign:"center",color:"#7d7d70",fontSize:14}}>No spending data yet</div>}
        </div>
      </div>

      <SpendingBreakdownSheet open={showBreakdown} onOpenChange={setShowBreakdown} leftover={leftover} formatCurrency={formatCurrency} />
    </div>
  );
};
